import type { CatalogueStrings } from "./fr";

export const catalogueEn: CatalogueStrings = {
  title: "Questionnaire content",
  eyebrow: "Administration",
  loading: "Loading…",
  error: "Something went wrong.",
  tabs: {
    thematics: "Themes",
    issues: "Issues",
    goals: "Objectives",
    actions: "Courses of action",
    sectors: "Sectors",
    characteristics: "Characteristics",
  },
  sectorCategories: "Sector categories",
  characteristicGroups: "Main characteristics",
  add: "Add",
  edit: "Edit",
  delete: "Delete",
  cancel: "Cancel",
  save: "Save",
  close: "Close",
  moveUp: "Move up",
  moveDown: "Move down",
  history: "History",
  historyTitle: "Modification history",
  recent: "Recent changes",
  recentTitle: "Recent modifications",
  missingTranslation: "Missing translation",
  noCategory: "No category",
  noGroup: "No main characteristic",
  searchPlaceholder: "Search…",
  empty: "No items.",
  noResults: "No results",
  addTitle: {
    thematic: "Add a theme",
    issue: "Add an issue",
    goal: "Add an objective",
    action: "Add a course of action",
    sector: "Add a sector",
    sector_category: "Add a sector category",
    characteristic: "Add a characteristic",
    characteristic_group: "Add a main characteristic",
  },
  editTitle: "Edit “{{name}}”",
  help: {
    experts_enabled:
      "Allow experts to attach to this theme. When unchecked, the theme (and its issues and courses of action) is not offered for selection during expert registration, and no expert is shown for its courses of action. Selections experts have already made are kept and reappear if the box is checked again.",
    tools_enabled:
      "Allow tools to be linked to this thematic's courses of action. If unchecked, no tool can be linked to its courses of action or shown for them.",
    financial_aids_enabled:
      "Allow financial aid to be linked to this thematic's courses of action. If unchecked, no aid can be linked to its courses of action or shown for them.",
  },
  fields: {
    name_fr: "Text (French)",
    name_en: "Text (English)",
    thematic: "Theme",
    issue: "Issue",
    goal: "Objective",
    category: "Category",
    group: "Main characteristic",
    prerequisite: "Prerequisite",
    css_class: "Color",
    icon: "Icon",
    colour: "Custom color",
    experts_enabled: "Experts allowed",
    tools_enabled: "Tools allowed",
    financial_aids_enabled: "Financial aid allowed",
    sectors: "Sectors",
    characteristics: "Characteristics",
    company_sizes: "Company sizes",
    unavoidable_issues: "Key issues",
    triggering_characteristics: "Triggering characteristics",
    offered_characteristics: "Offered characteristics",
    actions: "Filtered courses of action",
    issues: "Triggered issues",
  },
  restore: {
    button: "Restore",
    title: "Restore “{{name}}”",
    required: "The required parent no longer exists: {{field}} “{{name}}”. Choose another one below, or cancel to restore it first.",
    requiredUnnamed: "The required parent no longer exists: {{field}}. Choose another one below, or cancel to restore it first.",
    choose: "Choose…",
    warnings: "One or more linked items no longer exist and will not be restored:",
    continue: "Do you still want to continue?",
    alreadyRestored: "This item has already been restored.",
    error: "The restore failed. Try again.",
    done: "“{{name}}” has been restored.",
  },
  icon: {
    hint: "The icon's Font Awesome 5 class, for example “fas fa-leaf”: pick it from the list or copy it from Font Awesome's free icon gallery.",
    gallery: "Font Awesome icon gallery",
    choose: "Choose an icon",
    loading: "Loading icons…",
    empty: "No icon matches this search.",
    search: "Search for an icon",
    searchHint: "The search uses <gallery>Font Awesome</gallery>'s English names, for example “leaf” or “recycle”.",
    unavailable: "The icon list is not available. Choose an icon in the Font Awesome gallery, then type its class in the field.",
  },
  none: "None",
  emptyMeansAll: "Leave empty for every enterprise.",
  colours: {
    governance: "Governance",
    economic: "Economic",
    social: "Social",
    environment: "Environmental",
    custom: "Custom",
  },
  colour: {
    hint: "Hexadecimal code, for example #C2410C.",
    preview: "Preview",
    fill: "Banner",
    ink: "Text",
    soft: "Pale background",
    unreadable:
      "Neither dark nor white text is readable on this color: choose it a little lighter or darker.",
  },
  impact: {
    title: "Confirm change",
    intro: "This change affects existing enterprises:",
    confirm: "Confirm and save",
    stale: "The numbers changed since the preview. Check them again.",
  },
  deletion: {
    title: "Delete “{{name}}”?",
    permanent: "This deletion is permanent.",
    blocked: "Cannot delete: data depends on this item.",
    blockedHelp: "Remove these dependencies before deleting the item.",
  },
  saved: "“{{name}}” saved.",
  deleted: "“{{name}}” deleted.",
  dashboard: {
    subtitle: {
      actions: "Edit action text (French / English) and manage expert access.",
      goals:
        "Edit goal text (French / English) and designate the goals presented to demo accounts.",
      issues: "Edit issue text (French / English).",
    },
    search: {
      actions: "Search for an action…",
      goals: "Search for an objective…",
      issues: "Search for an issue…",
    },
    filters: {
      thematic: "Theme",
      issue: "Issue",
      goal: "Objective",
      english: "English translation",
      allFeminine: "All",
      allMasculine: "All",
      missing: "Missing",
      present: "Present",
      reset: "Reset",
    },
    count: {
      actions_one: "{{count}} course of action",
      actions_other: "{{count}} courses of action",
      actionsFiltered_one: "{{count}} course of action out of {{total}}",
      actionsFiltered_other: "{{count}} courses of action out of {{total}}",
      goals_one: "{{count}} objective",
      goals_other: "{{count}} objectives",
      goalsFiltered_one: "{{count}} objective out of {{total}}",
      goalsFiltered_other: "{{count}} objectives out of {{total}}",
      issues_one: "{{count}} issue",
      issues_other: "{{count}} issues",
      issuesFiltered_one: "{{count}} issue out of {{total}}",
      issuesFiltered_other: "{{count}} issues out of {{total}}",
    },
    bannerStrong: "Saving fixes the text everywhere.",
    banner: {
      actions:
        "Editing an action updates its wording in existing action plans (except those renamed by the enterprise) and in already-published communication pages that use it.",
      goals:
        "Editing an objective updates its wording in existing action plans (except those renamed by the enterprise) and in already-published communication pages that use it.",
      issues:
        "Editing an issue updates its wording in existing action plans and in already-published communication pages that use it.",
    },
    text: {
      actions: "Action text",
      goals: "Objective text",
      issues: "Issue text",
    },
    textEn: "Text (English)",
    canHaveExperts: "Can have experts",
    availableInDemo: "Available in demonstration",
    pending: "Pending",
    pendingTitle: "Change awaiting validation by another person",
    on: "on",
    off: "off",
    modified: "Modified",
    plans: "plans",
    communications: "communications",
    experts: "experts",
    tools: "Tools",
    aids: "Aid",
    toolOrderTitle: "Tool display order",
    aidOrderTitle: "Financial aid display order",
    noMatch: {
      actions: "No action matches your search.",
      goals: "No objective matches your search.",
      issues: "No issue matches your search.",
    },
    dirty: {
      actions: "action(s) modified",
      goals: "objective(s) modified",
      issues: "issue(s) modified",
    },
    savedCount: {
      actions: "{{n}} action(s) saved.",
      goals: "{{n}} objective(s) saved.",
      issues: "{{n}} issue(s) saved.",
    },
    stagedCount: {
      actions: "{{n}} expert-access change(s) awaiting validation.",
      goals: "{{n}} demonstration availability change(s) awaiting validation.",
    },
    saveChanges: "Save changes",
  },
  review: {
    approved: {
      "expert-access": "{{n}} expert-access change(s) approved.",
      demo: "{{n}} demonstration availability change(s) approved.",
    },
    nothingToApprove: "No change to approve.",
    countered: "{{n}} counter-proposal(s) recorded, awaiting another person.",
    nothingToReview: "No change to review.",
    noteRequired: "A note is required for a counter-proposal.",
    validate: "To validate",
    validatePending: "Changes awaiting your validation",
    validateNone: "No validation pending",
    title: {
      "expert-access": "Expert access · to validate",
      demo: "Demonstration availability · to validate",
    },
    introReview: "Proposed by other people.",
    introReviewStrong: "You cannot validate your own proposals.",
    introPropose: {
      "expert-access":
        "Choose the value you want for each course of action, then justify it. Your proposal will need validation in turn.",
      demo: "Choose the value you want for each goal, then justify it. Your proposal will need validation in turn.",
    },
    proposedBy: "Proposed by",
    deletedUser: "Deleted user",
    proposedValue: "Proposed value",
    enabled: "Enabled",
    disabled: "Disabled",
    note: "Note",
    required: "required",
    notePlaceholder: "Explain your counter-proposal…",
    proposals: "proposal(s)",
    later: "Later",
    propose: "Propose changes",
    submitCounter: "Submit counter-proposal",
    approveAll: "Approve all",
  },
};
